import React, { useState, useEffect, useCallback } from 'react';
import { shareLinksApi } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LinkSimple, MagnifyingGlass, Copy, Trash, Eye, Folder, File,
  Clock, ShareNetwork, Download, Lock, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ShareLinksDashboardPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await shareLinksApi.getAll();
      setLinks(res.data);
    } catch {
      toast.error('Failed to load share links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const isExpired = (link) => {
    if (!link.expires_at) return false;
    try { return new Date(link.expires_at) < new Date(); } catch { return false; }
  };

  const filtered = links.filter((l) => {
    if (search && !(l.resource_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== 'all' && l.resource_type !== typeFilter) return false;
    if (statusFilter === 'active' && !(l.is_active && !isExpired(l))) return false;
    if (statusFilter === 'revoked' && l.is_active) return false;
    if (statusFilter === 'expired' && !isExpired(l)) return false;
    return true;
  });

  const activeCount = links.filter(l => l.is_active && !isExpired(l)).length;
  const revokedCount = links.filter(l => !l.is_active).length;
  const expiredCount = links.filter(l => isExpired(l)).length;
  const totalViews = links.reduce((sum, l) => sum + (l.view_count || 0), 0);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.filter(l => l.is_active).length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.filter(l => l.is_active).map(l => l.id)));
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this link? Anyone using it will immediately lose access.')) return;
    try {
      await shareLinksApi.revoke(id);
      toast.success('Link revoked');
      fetchLinks();
    } catch { toast.error('Failed to revoke'); }
  };

  const handleBulkRevoke = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Revoke ${selected.size} link${selected.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await Promise.all([...selected].map(id => shareLinksApi.revoke(id)));
      toast.success(`${selected.size} link${selected.size > 1 ? 's' : ''} revoked`);
      setSelected(new Set());
      fetchLinks();
    } catch { toast.error('Some revocations failed'); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  const getStatus = (link) => {
    if (!link.is_active) return { label: 'Revoked', variant: 'destructive' };
    if (isExpired(link)) return { label: 'Expired', variant: 'outline' };
    return { label: 'Active', variant: 'default' };
  };

  return (
    <div className="space-y-6" data-testid="share-links-dashboard">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Share Links</h1>
          <p className="text-muted-foreground text-sm mt-1">Monitor and manage all external share links</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CheckCircle} iconColor="text-emerald-500" label="Active" value={activeCount} />
        <StatCard icon={Clock} iconColor="text-amber-500" label="Expired" value={expiredCount} />
        <StatCard icon={Lock} iconColor="text-destructive" label="Revoked" value={revokedCount} />
        <StatCard icon={Eye} iconColor="text-blue-500" label="Total Views" value={totalViews} />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input placeholder="Search by resource name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="folder">Folders</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="expired">Expired only</SelectItem>
            <SelectItem value="revoked">Revoked only</SelectItem>
          </SelectContent>
        </Select>
        {selected.size > 0 && (
          <Button variant="destructive" onClick={handleBulkRevoke} data-testid="bulk-revoke-btn">
            <Trash size={16} className="mr-1" /> Revoke {selected.size}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <LinkSimple size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
            <p className="text-muted-foreground">{links.length === 0 ? 'No share links yet' : 'No links match your filters'}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.filter(l => l.is_active).length}
                    onChange={toggleSelectAll}
                    className="rounded"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Resource</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Status</th>
                <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Views</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Last Viewed</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Expires</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Access</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Created</th>
                <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((link) => {
                const status = getStatus(link);
                const Icon = link.resource_type === 'folder' ? Folder : File;
                return (
                  <tr key={link.id} className="border-b border-border hover:bg-muted/30" data-testid={`link-row-${link.id}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(link.id)}
                        onChange={() => toggleSelect(link.id)}
                        disabled={!link.is_active}
                        className="rounded"
                        data-testid={`select-${link.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${link.resource_type === 'folder' ? 'bg-amber-500/10' : 'bg-primary/10'}`}>
                          <Icon size={16} className={link.resource_type === 'folder' ? 'text-amber-600' : 'text-primary'} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{link.resource_name || '(unavailable)'}</p>
                          <p className="text-xs text-muted-foreground capitalize">{link.resource_type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={status.variant === 'default' ? undefined : status.variant}
                        className={status.variant === 'default' ? 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20' : ''}>
                        {status.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-center text-sm font-medium">{link.view_count || 0}</td>
                    <td className="p-3 text-xs text-muted-foreground">{link.last_viewed_at ? formatDateTime(link.last_viewed_at) : '—'}</td>
                    <td className="p-3 text-xs text-muted-foreground">{link.expires_at ? formatDate(link.expires_at) : 'Never'}</td>
                    <td className="p-3">
                      {link.allow_download ? (
                        <Badge variant="outline" className="text-xs"><Download size={10} className="mr-1" /> DL</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs opacity-60"><Eye size={10} className="mr-1" /> View</Badge>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{formatDate(link.created_at)}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        {link.is_active && (
                          <Button variant="outline" size="sm" onClick={() => copyLink(link.token)} data-testid={`copy-${link.id}`}>
                            <Copy size={14} />
                          </Button>
                        )}
                        {link.is_active && (
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleRevoke(link.id)} data-testid={`revoke-${link.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, label, value }) {
  return (
    <div className="bg-card border border-border p-4 rounded-xl">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${iconColor}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
}
